import React, { useState, useRef } from 'react'
import { renderText } from 'stream-chat-react'
import DOMPurify from 'dompurify'
import { useFloating, autoUpdate, flip, shift, offset } from '@floating-ui/react'
import { AppAvatar } from '../../../Common/AppAvatar/AppAvatar.component'
import { getInitials } from '../../helper'
import { formatFileSize } from 'utils/helper'
import copyToClipboard from 'utils/copyToClipboard'
import { ReactComponent as StarIconSvg } from '../../../../assets/svgs/star-icon.svg'
import { ReactComponent as ThreeDotsIconSvg } from '../../../../assets/svgs/three-dots-icon.svg'
import { ReactComponent as CopyIconSvg } from '../../../../assets/svgs/copy-icon.svg'
import { ReactComponent as DownloadIcon } from '../../../../assets/svgs/dowload-icon.svg'
import { ReactComponent as FileIcon } from '../../../../assets/svgs/file-icon-2.svg'
import { VoiceMessage } from '../VoiceMessage'

// Three Dots Icon Component
const ThreeDotsIcon = () => <ThreeDotsIconSvg className="w-4 h-4 text-app-grey-60" />

const CopyIcon = () => <CopyIconSvg className="w-4 h-4 text-app-grey-60" />

// Star Icon Component wrapper to handle filled state
const StarIcon = ({ filled = false, color = 'text-app-grey-65' }) => (
  <StarIconSvg
    className={`w-5 h-5 ${color}`}
    style={{
      stroke: filled ? 'none' : 'currentColor',
      fill: filled ? 'currentColor' : 'none',
      strokeWidth: filled ? '0' : '1.25',
    }}
  />
)

// Function to highlight search terms
const highlightSearchText = (text: string, query: string) => {
  if (!query || !query.trim() || !text) return text

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedQuery})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part, index) => {
    if (regex.test(part)) {
      return (
        <span key={index} className="bg-app-orange-20 text-app-orange-100 rounded font-medium">
          {part}
        </span>
      )
    }
    return part
  })
}

// Helper function to sanitize HTML and ensure links open in new tab
const sanitizeAndProcessLinks = (html: string): string => {
  // First sanitize with DOMPurify
  const sanitized = DOMPurify.sanitize(html, {
    ADD_TAGS: ['mention'],
    ADD_ATTR: ['id', 'target', 'rel', 'href'],
  })

  // Create a temporary DOM element to process links
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = sanitized

  // Find all anchor tags and add target="_blank" and rel="noopener noreferrer"
  const links = tempDiv.querySelectorAll('a')
  links.forEach((link) => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })

  return tempDiv.innerHTML
}

// Message Actions Dropdown Menu for starred messages
interface StarredMessageActionsMenuProps {
  isVisible: boolean
  onUnstar: () => void
  message: any
  floatingRef?: (node: HTMLDivElement | null) => void
  floatingStyles?: React.CSSProperties
}

const StarredMessageActionsMenu = ({
  isVisible,
  onUnstar,
  message,
  floatingRef,
  floatingStyles,
}: StarredMessageActionsMenuProps) => {
  if (!isVisible) return null

  const menuItems = [
    {
      icon: <StarIcon filled={true} />,
      label: 'Remove star',
      action: onUnstar,
    },
  ]

  return (
    <div
      ref={floatingRef}
      style={floatingStyles}
      className="bg-app-white-100 rounded-lg shadow-lg border border-app-grey-25 py-2 min-w-[160px] z-50"
    >
      {menuItems.map((item, index) => (
        <button
          key={index}
          onClick={item.action}
          className="w-full px-4 py-2 text-left text-sm text-app-grey-70 hover:bg-app-grey-5 flex items-center gap-3"
        >
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {item.icon}
          </span>
          <span className="text-app-grey-100">{item.label}</span>
        </button>
      ))}
    </div>
  )
}

// Custom Message Text component that formats mentions and rich text
const CustomMessageText = ({ message, isMyMessage, searchQuery }) => {
  const hasText = message.text && message.text.trim().length > 0
  const hasAttachments = message.attachments && message.attachments.length > 0
  const hasTranscript = message.custom?.transcript && message.custom.transcript.trim().length > 0
  const senderName = message.user
    ? `${message.user.firstName ?? message.user.name ?? ''} ${message.user.lastName ?? ''}`.trim()
    : ''
  const sharedDate = message.created_at
    ? new Date(message.created_at).toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : ''

  const downloadFile = async (url?: string, filename?: string) => {
    if (!url) return
    try {
      // For images, fetch as blob to ensure proper download
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      // Fallback to direct link if fetch fails (CORS issues)
      console.error('Failed to download as blob, using direct link:', error)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || 'download'
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const renderImageAttachment = (attachment, idx) => (
    <div key={`image-${idx}`} className="relative group">
      <div className="relative w-[100px] h-[100px] rounded-xl overflow-hidden">
        <img
          src={attachment.image_url}
          alt={attachment.fallback || attachment.title || 'Image attachment'}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => window.open(attachment.image_url, '_blank')}
        />

        <button
          onClick={async (e) => {
            e.preventDefault()
            e.stopPropagation()
            await downloadFile(
              attachment.image_url,
              attachment.fallback || attachment.title || `image-${idx}.jpg`
            )
          }}
          className="absolute top-2 right-2 bg-white/90 hover:bg-black/80 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all shadow-lg !text-app-black-100 hover:!text-white"
          title="Download image"
          aria-label="Download image"
        >
          <DownloadIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )

  const renderFileAttachment = (attachment, idx) => {
    const fileName = attachment.title || attachment.file_name || attachment.name || 'File'
    const fileSizeText = attachment.file_size ? formatFileSize(attachment.file_size) : ''
    const sharedMeta =
      senderName && sharedDate ? `Shared by ${senderName} on ${sharedDate}` : fileSizeText

    return (
      <div
        key={`file-${idx}`}
        className="flex items-center gap-2.5 bg-app-white-100 border border-app-grey-25 rounded-2xl p-2.5 w-full shadow-sm !text-app-black-100"
      >
        <div className="w-9 h-9 rounded-xl bg-app-primary-60 flex items-center justify-center flex-shrink-0">
          <FileIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="font-semibold !text-app-black-100 truncate text-sm leading-tight">
            {fileName}
          </div>
          {sharedMeta && (
            <div className="text-xs text-app-grey-70 leading-tight mt-1">{sharedMeta}</div>
          )}
          {fileSizeText && sharedMeta !== fileSizeText && (
            <div className="text-[11px] text-app-grey-60 leading-tight mt-1">{fileSizeText}</div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.preventDefault()
            downloadFile(
              attachment.asset_url || attachment.file_url || attachment.url,
              attachment.title || attachment.file_name || 'download'
            )
          }}
          className="p-1 rounded-full bg-app-grey-5 hover:bg-app-grey-20 transition-colors text-app-primary-60"
          title="Download file"
          aria-label="Download file"
        >
          <DownloadIcon className="w-4 h-4 text-app-primary-60" />
        </button>
      </div>
    )
  }

  // Check for voice messages in attachments - more comprehensive detection
  const voiceAttachments = hasAttachments
    ? message.attachments.filter((att) => {
        if (att.type !== 'file' && att.type !== 'audio') {
          return false
        }

        const allFields = JSON.stringify(att).toLowerCase()
        if (allFields.includes('voice-message')) {
          return true
        }

        if (att.mime_type?.startsWith('audio/')) {
          return true
        }

        const fileName = att.file_name || att.name || att.title || att.fallback || att.text || ''
        if (
          typeof fileName === 'string' &&
          (fileName.endsWith('.webm') || fileName.includes('voice-message'))
        ) {
          return true
        }

        const url = att.asset_url || att.file_url || att.url || ''
        if (typeof url === 'string' && url.includes('voice-message')) {
          return true
        }

        if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) {
          return true
        }

        return false
      })
    : []

  const nonVoiceAttachments = hasAttachments
    ? message.attachments.filter((att) => {
        if (att.type !== 'file' && att.type !== 'audio') return true

        const allFields = JSON.stringify(att).toLowerCase()
        if (allFields.includes('voice-message')) return false

        if (att.mime_type?.startsWith('audio/')) return false

        const fileName = att.file_name || att.name || att.title || att.fallback || att.text || ''
        if (
          typeof fileName === 'string' &&
          (fileName.endsWith('.webm') || fileName.includes('voice-message'))
        )
          return false

        const url = att.asset_url || att.file_url || att.url || ''
        if (typeof url === 'string' && url.includes('voice-message')) return false

        if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) return false

        return true
      })
    : []

  const imageAttachments = nonVoiceAttachments.filter(
    (att) => att.type === 'image' && !!att.image_url
  )
  const fileAttachments = nonVoiceAttachments.filter((att) => att.type === 'file')

  if (!hasText && !hasAttachments && !hasTranscript) {
    return <div className="px-4 py-2 text-sm"></div>
  }

  if (voiceAttachments.length > 0) {
    return (
      <div className="px-2 py-2 text-sm text-left w-full max-w-full text-inherit">
        {/* Render voice messages */}
        <div className={imageAttachments.length > 0 || fileAttachments.length > 0 ? 'mb-2' : ''}>
          {voiceAttachments.map((attachment, index) => {
            const audioUrl = attachment.asset_url || attachment.file_url || attachment.url

            // Prepare text content to display inside voice message container
            const textContent =
              hasText || message.custom?.richTextHtml || message.html ? (
                <div className="text-left text-white [&_*]:text-white">
                  {message.custom?.richTextHtml ? (
                    <div
                      className="prose prose-sm max-w-none [&_a]:text-white [&_a:hover]:text-white/80 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-white/20 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-white [&_mention]:font-medium"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeAndProcessLinks(message.custom.richTextHtml),
                      }}
                    />
                  ) : message.html ? (
                    <div
                      className="prose prose-sm max-w-none [&_a]:text-white [&_a:hover]:text-white/80 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-white/20 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-white [&_mention]:font-medium"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeAndProcessLinks(message.html),
                      }}
                    />
                  ) : searchQuery ? (
                    <div className="text-white [&_span]:bg-yellow-300 [&_span]:text-app-black-100">
                      {highlightSearchText(message.text, searchQuery)}
                    </div>
                  ) : (
                    <div className="text-white">{renderText(message.text)}</div>
                  )}
                </div>
              ) : undefined

            return (
              <VoiceMessage
                key={`voice-${index}`}
                audioUrl={audioUrl}
                duration={message.custom?.voiceDurationSeconds}
                transcript={hasTranscript ? message.custom.transcript : undefined}
                isMyMessage={isMyMessage}
                className="mb-2 last:mb-0"
                textContent={textContent}
              />
            )
          })}
        </div>

        {/* Render non-voice attachments */}
        {(imageAttachments.length > 0 || fileAttachments.length > 0) && (
          <div className={`${hasText || hasTranscript ? 'mt-2' : ''} space-y-3`}>
            {imageAttachments.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {imageAttachments.map((attachment, idx) => renderImageAttachment(attachment, idx))}
              </div>
            )}
            {fileAttachments.length > 0 && (
              <div className="space-y-2">
                {fileAttachments.map((attachment, idx) => renderFileAttachment(attachment, idx))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // For regular messages, render with padding
  return (
    <div className="px-2 py-2 text-sm text-left w-full max-w-full text-inherit">
      {/* Render text content */}
      {(hasText || message.custom?.richTextHtml || message.html) && (
        <div className="text-left">
          {message.custom?.richTextHtml ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-primary-60 [&_a:hover]:text-app-primary-70 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeAndProcessLinks(message.custom.richTextHtml),
              }}
            />
          ) : message.html ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-primary-60 [&_a:hover]:text-app-primary-70 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeAndProcessLinks(message.html),
              }}
            />
          ) : searchQuery ? (
            highlightSearchText(message.text, searchQuery)
          ) : (
            renderText(message.text)
          )}
        </div>
      )}

      {/* Render transcript if available */}
      {hasTranscript && (
        <div className="text-left text-app-grey-70 italic">
          <span className="text-xs text-app-grey-60 mr-1">Transcript:</span>
          {message.custom.transcript}
        </div>
      )}

      {/* Render non-voice attachments */}
      {(imageAttachments.length > 0 || fileAttachments.length > 0) && (
        <div className={`${hasText || hasTranscript ? 'mt-2' : ''} space-y-3`}>
          {imageAttachments.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {imageAttachments.map((attachment, idx) => renderImageAttachment(attachment, idx))}
            </div>
          )}
          {fileAttachments.length > 0 && (
            <div className="space-y-2">
              {fileAttachments.map((attachment, idx) => renderFileAttachment(attachment, idx))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface StarredMessageItemProps {
  message: any
  client: any
  onUnstar: (messageId: string) => Promise<void>
}

export const StarredMessageItem: React.FC<StarredMessageItemProps> = ({
  message,
  client,
  onUnstar,
}) => {
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const messageRef = useRef<HTMLDivElement>(null)

  // Floating UI for menu positioning
  const { refs, floatingStyles } = useFloating({
    open: showActionsMenu,
    onOpenChange: setShowActionsMenu,
    placement: 'bottom-end',
    middleware: [
      offset(4),
      flip({
        fallbackPlacements: ['top-end', 'bottom-start', 'top-start'],
      }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const isMyMessage = () => {
    return client?.userID === message.user?.id
  }

  const handleThreeDotsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowActionsMenu(!showActionsMenu)
  }

  const handleCopy = async () => {
    if (message?.text) {
      try {
        await copyToClipboard(message.text)
      } catch (err) {
        console.error('Failed to copy: ', err)
      }
    }
  }

  const handleUnstar = async () => {
    try {
      if (client) {
        await client.unpinMessage(message)
        await onUnstar(message.id)
      }
    } catch (error) {
      console.error('Error unstarring message:', error)
    }
    setShowActionsMenu(false)
  }

  // Close menu when clicking outside
  React.useEffect(() => {
    if (!showActionsMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const referenceElement = refs.reference.current
      const floatingElement = refs.floating.current

      if (
        floatingElement &&
        !floatingElement.contains(target) &&
        referenceElement &&
        referenceElement instanceof HTMLElement &&
        !referenceElement.contains(target)
      ) {
        setShowActionsMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showActionsMenu, refs])

  const messageClasses = isMyMessage()
    ? 'str-chat__message str-chat__message--me str-chat__message-simple str-chat__message-simple--me'
    : 'str-chat__message str-chat__message-simple'

  return (
    <div className={`${messageClasses}`} ref={messageRef}>
      <div className="mt-2 w-full relative">
        <div className={`flex gap-3 ${isMyMessage() ? 'justify-end' : 'justify-start'}`}>
          {!isMyMessage() && (
            <AppAvatar
              size="md"
              type="mixed"
              shape="Rounded"
              text={getInitials({
                firstName: message.user?.firstName as string,
                lastName: message.user?.lastName as string,
              })}
            />
          )}
          <div
            className={`flex flex-col w-full overflow-visible ${
              isMyMessage() ? 'items-end' : 'items-start'
            }`}
          >
            <div
              className={`text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1 ${
                isMyMessage() ? 'mr-1 justify-end' : 'ml-1 justify-start'
              }`}
            >
              <span>
                {isMyMessage() ? 'You' : `${message.user?.firstName} ${message.user?.lastName}`}
              </span>
            </div>
            <div className="relative">
              {/* Check if message has voice attachments to render differently */}
              {message.attachments?.some((att) => {
                if (att.type !== 'file' && att.type !== 'audio') return false

                // AGGRESSIVE DETECTION: Check for voice-message pattern in any field
                const allFields = JSON.stringify(att).toLowerCase()
                if (allFields.includes('voice-message')) return true

                if (att.mime_type?.startsWith('audio/')) return true
                const fileName =
                  att.file_name || att.name || att.title || att.fallback || att.text || ''
                if (
                  typeof fileName === 'string' &&
                  (fileName.endsWith('.webm') || fileName.includes('voice-message'))
                )
                  return true
                const url = att.asset_url || att.file_url || att.url || ''
                if (typeof url === 'string' && url.includes('voice-message')) return true
                if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) return true
                return false
              }) ? (
                // Render voice message without background bubble
                <div className="flex items-start gap-1">
                  {message.pinned && (
                    <div
                      className={`flex-shrink-0 mt-2 rounded-full p-1 ${
                        isMyMessage() ? 'bg-white' : 'bg-app-black-100'
                      }`}
                      title="Starred message"
                    >
                      <StarIcon
                        filled={true}
                        color={isMyMessage() ? 'text-white' : 'text-app-black-100'}
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <CustomMessageText
                      message={message}
                      isMyMessage={isMyMessage()}
                      searchQuery={undefined}
                    />
                  </div>
                </div>
              ) : (
                // Render regular message with bubble
                <div
                  className={`rounded-2xl flex items-start gap-1 ${
                    isMyMessage()
                      ? 'bg-app-primary-125 text-app-white-100'
                      : 'bg-app-white-95 text-app-black-100'
                  } [&_*]:text-inherit [&_*]:text-[color:inherit]`}
                >
                  {message.pinned && (
                    <div className={`flex-shrink-0 ml-1.5 py-2`} title="Starred message">
                      <StarIcon
                        filled={true}
                        color={isMyMessage() ? 'text-white' : 'text-app-black-100'}
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <CustomMessageText
                      message={message}
                      isMyMessage={isMyMessage()}
                      searchQuery={undefined}
                    />
                  </div>
                </div>
              )}

              {/* Three dots menu button - appears on hover */}
            </div>

            <div className="text-xs text-silver-darker mt-1 px-1 flex items-center justify-end">
              <div className="flex">
                <div className="relative">
                  <button className="mx-1" onClick={handleCopy} role="menuitem" title="Copy">
                    <CopyIcon />
                  </button>
                  <button
                    ref={refs.setReference}
                    className="mx-1"
                    onClick={handleThreeDotsClick}
                    title="More options"
                  >
                    <ThreeDotsIcon />
                  </button>

                  {/* Actions dropdown menu */}
                  <StarredMessageActionsMenu
                    isVisible={showActionsMenu}
                    onUnstar={handleUnstar}
                    message={message}
                    floatingRef={refs.setFloating}
                    floatingStyles={floatingStyles}
                  />
                </div>
              </div>
            </div>
          </div>
          {isMyMessage() && (
            <AppAvatar
              size="md"
              type="mixed"
              shape="Rounded"
              text={getInitials({
                firstName: message.user?.firstName as string,
                lastName: message.user?.lastName as string,
              })}
            />
          )}
        </div>
      </div>
    </div>
  )
}
